import { BaseResultRow } from "@mirage/mosaics/Omnibox/components/ResultRow/ResultRowByType/BaseResultRow";
import { typeahead } from "@mirage/service-typeahead-search/service/types";
import type { ReactNode } from "react";
import { getDisplayResult } from "./adapter/getDisplayResult";
import { CurrentAskQueryResultRow } from "./ResultRowByType/CurrentAskQueryResultRow";
import { CurrentQueryResultRow } from "./ResultRowByType/CurrentQueryResultRow";
import { DefaultResultRow } from "./ResultRowByType/DefaultResultRow";
import { DesktopApplicationResultRow } from "./ResultRowByType/DesktopApplicationResultRow";
import { DraftFromSourceResultRow } from "./ResultRowByType/DraftFromSourceResultRow";
import { DraftFromTemplateResultRow } from "./ResultRowByType/DraftFromTemplateResultRow";
import { DraftFromToneResultRow } from "./ResultRowByType/DraftFromToneResultRow";
import { OrganizeUserQueryResultRow } from "./ResultRowByType/OrganizeUserQuery";
import { PeopleFilterResultRow } from "./ResultRowByType/PeopleFilterResultRow";
import { WithPreviousQueryResultRow } from "./ResultRowByType/PreviousQueryResultRow";
import { WithRecentChatResultRow } from "./ResultRowByType/RecentChatResultRow";
import { SearchFilterResultRow } from "./ResultRowByType/SearchFilterResultRow";
import { StackItemResultRow } from "./ResultRowByType/StackItemResultRow";
import { StackResultRow } from "./ResultRowByType/StackResultRow";
import { TitleAndIconResultRow } from "./ResultRowByType/TitleAndIconResultRow";
import { UrlShortcutResultRow } from "./ResultRowByType/UrlShortcutResultRow";

export const getCustomResultRow = ({
  resultType,
  scoredResult,
  isSelected,
  currentQuery,
  accessory,
}: {
  resultType: typeahead.ResultType;
  scoredResult: typeahead.ScoredResult;
  isSelected: boolean;
  currentQuery: string;
  accessory?: ReactNode;
}) => {
  switch (resultType) {
    case typeahead.ResultType.DraftWithSource:
      return (
        <DraftFromSourceResultRow
          scoredResult={scoredResult as typeahead.ScoredDraftWithSource}
          isSelected={isSelected}
        />
      );
    case typeahead.ResultType.DraftFromTemplate:
      return (
        <DraftFromTemplateResultRow
          scoredResult={scoredResult as typeahead.ScoredDraftFromTemplate}
          isSelected={isSelected}
        />
      );
    case typeahead.ResultType.DraftFromTone:
      return (
        <DraftFromToneResultRow
          scoredResult={scoredResult as typeahead.ScoredDraftFromTone}
          isSelected={isSelected}
        />
      );
    case typeahead.ResultType.URLShortcut:
      return (
        <UrlShortcutResultRow
          scoredResult={scoredResult as typeahead.ScoredURLShortcut}
          isSelected={isSelected}
        />
      );
    case typeahead.ResultType.PreviousQuery:
      return (
        <WithPreviousQueryResultRow
          scoredResult={scoredResult as typeahead.ScoredPreviousQuery}
          isSelected={isSelected}
        />
      );
    case typeahead.ResultType.StackItem:
      return (
        <StackItemResultRow
          scoredResult={scoredResult as typeahead.ScoredStackItem}
          isSelected={isSelected}
        />
      );
    case typeahead.ResultType.Stack:
      return (
        <StackResultRow
          scoredResult={scoredResult as typeahead.ScoredStack}
          isSelected={isSelected}
        />
      );
    case typeahead.ResultType.SuggestedQuery: {
      const { title, subtitle } = getDisplayResult(
        currentQuery,
        scoredResult as typeahead.ScoredSuggestedQuery,
      );
      return (
        <CurrentQueryResultRow
          iconType={"search"}
          title={title as string}
          subtitle={subtitle as string}
          isSelected={isSelected}
          currentQuery={currentQuery}
        />
      );
    }
    case typeahead.ResultType.AskUserQuery: {
      const { title, subtitle } = getDisplayResult(
        currentQuery,
        scoredResult as typeahead.ScoredAskUserQuery,
      );
      return (
        <CurrentAskQueryResultRow
          title={title as string}
          subtitle={subtitle as string}
          isSelected={isSelected}
        />
      );
    }
    case typeahead.ResultType.WriteUserQuery: {
      const { title, subtitle } = getDisplayResult(
        currentQuery,
        scoredResult as typeahead.ScoredWriteUserQuery,
      );
      return (
        <CurrentQueryResultRow
          iconType={"edit"}
          title={title as string}
          subtitle={subtitle as string}
          isSelected={isSelected}
        />
      );
    }
    case typeahead.ResultType.OrganizeUserQuery:
      return (
        <OrganizeUserQueryResultRow
          scoredResult={scoredResult as typeahead.ScoredOrganizeUserQuery}
          isSelected={isSelected}
        />
      );
    case typeahead.ResultType.AskSuggestion: {
      const { title } = getDisplayResult(
        currentQuery,
        scoredResult as typeahead.ScoredAskSuggestion,
      );
      return (
        <TitleAndIconResultRow
          iconType={"twinkle"}
          title={title as string}
          currentQuery={currentQuery}
        />
      );
    }
    case typeahead.ResultType.RecentChat:
      return (
        <WithRecentChatResultRow
          scoredResult={scoredResult as typeahead.ScoredRecentChat}
          isSelected={isSelected}
        />
      );
    case typeahead.ResultType.DesktopApplication:
      // NOTE (AYC): missing stories
      return (
        <DesktopApplicationResultRow
          scoredResult={scoredResult as typeahead.ScoredDesktopApplication}
          isSelected={isSelected}
        />
      );
    case typeahead.ResultType.PeopleFilter:
      // NOTE (AYC): missing stories
      return (
        <PeopleFilterResultRow
          scoredResult={scoredResult as typeahead.ScoredPeopleFilter}
          isSelected={isSelected}
          currentQuery={currentQuery}
        />
      );
    case typeahead.ResultType.SearchFilter: {
      const { icon, subtitle } = getDisplayResult(
        currentQuery,
        scoredResult as typeahead.ScoredSearchFilter,
      );
      return (
        <SearchFilterResultRow icon={icon} subtitle={subtitle as string} />
      );
    }
    case typeahead.ResultType.CalendarEvent: {
      const { icon, title, subtitle } = getDisplayResult(
        currentQuery,
        scoredResult as typeahead.ScoredCalendarEvent,
      );
      return (
        <BaseResultRow
          icon={icon}
          title={title as string}
          subtitle={subtitle as string}
          accessory={accessory}
          showAccessory={!!accessory}
        />
      );
    }
    case typeahead.ResultType.MathCalculation: {
      const { icon, title, subtitle } = getDisplayResult(
        currentQuery,
        scoredResult as typeahead.ScoredMathCalculation,
      );
      return (
        <BaseResultRow
          icon={icon}
          title={title as string}
          subtitle={subtitle as string}
        />
      );
    }
    case typeahead.ResultType.Recommendation:
    case typeahead.ResultType.SearchResult:
    case typeahead.ResultType.DesktopFile:
    case typeahead.ResultType.PeopleRecommendation:
    case typeahead.ResultType.MyProfileSuggestion:
    case typeahead.ResultType.Skeleton: {
      const { icon, title, subtitle } = getDisplayResult(
        currentQuery,
        scoredResult as Extract<
          typeahead.ScoredResult,
          {
            type:
              | typeahead.ResultType.Recommendation
              | typeahead.ResultType.SearchResult
              | typeahead.ResultType.MathCalculation
              | typeahead.ResultType.DesktopFile
              | typeahead.ResultType.PeopleRecommendation
              | typeahead.ResultType.MyProfileSuggestion
              | typeahead.ResultType.Skeleton;
          }
        >,
      );
      return (
        <DefaultResultRow
          icon={icon}
          title={title as string}
          subtitle={subtitle as string}
          currentQuery={currentQuery}
          isSkeleton={resultType === typeahead.ResultType.Skeleton}
        />
      );
    }
    default:
      resultType satisfies typeahead.ResultType.LoadingCompletion;
      throw new Error(`Unsupported result type: ${resultType}`);
  }
};
